import { preferencesMutationOptions, preferencesRequest } from "@/hooks/common/user/use-user-preferences.cache";
import { useApiSession } from "@Context/common/general/api-session-context";
import { useOptionalNotificationDialog } from "@Context/common/general/notification-dialog-context";
import { PreferenceUpdate, UserApis } from "@Shared/schemas/users/userSchemas";
import useApiMutation from "@hooks/common/general/use-api-mutation";
import useApiQuery from "@hooks/common/general/use-api-query";
import { useCallback } from "react";

export default function useUserPreferences({ enabled = true } = {}) {
  const notifications = useOptionalNotificationDialog();
  const userId = useApiSession() ?? "";
  const query = useApiQuery({
    api: UserApis.Users.GetPreferences,
    request: preferencesRequest,
    queryKey: [],
    enabled: enabled && Boolean(userId),
    staleTime: Infinity,
    gcTime: Infinity,
    disableLoader: true,
  });
  const mutationOptions = preferencesMutationOptions(userId);
  const mutation = useApiMutation({
    api: UserApis.Users.UpdatePreferences,
    ...mutationOptions,
    onSuccess: async (...args) => {
      await mutationOptions.onSuccess?.(...args);
      notifications?.showNotificationToast("settings.save.success", "success");
    },
    onError: () => notifications?.showNotificationToast("settings.save.error", "error"),
    disableLoader: true,
  });
  const { mutate, mutateAsync } = mutation;
  const updatePreferences = useCallback(
    (preferences: PreferenceUpdate[], options?: Parameters<typeof mutate>[1]) => mutate({ action: "UPDATE", preferences }, options),
    [mutate]
  );
  const updatePreferencesAsync = useCallback((preferences: PreferenceUpdate[]) => mutateAsync({ action: "UPDATE", preferences }), [mutateAsync]);
  return {
    ...query,
    preferences: query.data,
    updatePreferences,
    updatePreferencesAsync,
    updating: mutation.isPending,
    updateError: mutation.error,
  };
}
